import { getSphereSprite, shadingStrengthAbove } from './markShading';
import type { CameraState, NumericRange, Size, XyzDataPoint } from '../types';
import {
  clampPitch,
  createProjector,
  getBackCorner,
  getCameraBasis,
} from '../geometry/projection';
import { KEY_LIGHT, toWorldLightDirection } from './lighting';
import { isSameCamera } from '../interaction/orbitCamera';
import { hasColorField, isSameRange } from '../data/ranges';
import { toCssColor, type ColorScale, type Rgb } from './colorScale';
import { createRampLookup } from './rampLookup';
import type { UnitPoint } from './axisBox';
import {
  DEFAULT_POINTS_LOOK,
  type MarkContactLook,
  type MarkOutlineLook,
  type MarkShadowLook,
  type PointsLook,
  type WallProjectionLook,
} from './pointsLook';

/** Smallest mark drawn, in pixels of diameter. Below this a point stops being a shape. */
export const MINIMUM_DIAMETER = 8;

/**
 * How faint a mark goes when something else is highlighted.
 *
 * Exported for the tests rather than copied into them. `DIMMED * opacity` is the one alpha that
 * says "a mark was dimmed", so the probes that check whether dimming happened have to be keyed to
 * it - and a literal in the test would drift the moment either number moved, which is how two
 * tests here came to be measuring a bucket their subject had left.
 */
export const DIMMED = 0.15;

/** Stroke width of a mark whose size field has no value. */
const HOLLOW_MARK_WIDTH = 1.5;

const SEPARATION_RING_WIDTH = 2;

/** Ring marking a component the host named, drawn outside the separation ring. */
const EMPHASIS_RING_WIDTH = 1.5;

export type Points3dStyle = {
  /**
   * Vary mark area with the radius field. Off for the scatter variant, where every mark is
   * the same size and only position carries meaning.
   */
  showSizeVariation: boolean;
  /** Largest mark diameter in pixels, at the top of the radius field's range. */
  maximumDiameter: number;
  opacity: number;
};

export const DEFAULT_POINTS_STYLE: Points3dStyle = {
  showSizeVariation: true,
  maximumDiameter: 34,
  opacity: 0.92,
};

export type Points3dRenderArgs = {
  context: CanvasRenderingContext2D;
  points: XyzDataPoint[];
  camera: CameraState;
  viewport: Size;
  colorScale: ColorScale;
  style: Points3dStyle;
  xRange: NumericRange;
  yRange: NumericRange;
  zRange: NumericRange;
  radiusRange: NumericRange;
  /** Painted behind the separation ring, so marks read as separate objects. */
  surfaceColor: string;
  inkColor: string;
  /** Marks matching these ids are drawn at full strength and the rest are dimmed. */
  /** Suppresses everything else. Built for the hover, which lasts as long as the pointer rests. */
  highlightedIds: Set<string> | null;
  /** Adds a ring, and takes nothing away. For state that outlives a gesture, such as context. */
  emphasisedIds: Set<string> | null;
  look?: PointsLook;
};

type PlacedMark = {
  unit: UnitPoint;
  screenX: number;
  screenY: number;
  depth: number;
  diameter: number;
  color: string;
  isHighlighted: boolean;
  /**
   * Named by the host - in Ardoq, the component the reader arrived from.
   *
   * Kept apart from `isHighlighted` because the two last for different lengths of time. A hover
   * lasts as long as the pointer rests, so suppressing everything else is a fair answer to it. A
   * context component lasts as long as the view is open, and the same answer leaves the chart
   * permanently at 14% with no interaction that clears it - so this one adds a mark rather than
   * taking the others away.
   */
  isEmphasised: boolean;
  /** False when the size field is selected and this component has no value for it. */
  hasSize: boolean;
  /**
   * False when a colour field is selected and this component has no value for it.
   *
   * Kept apart from substituting z, which is what the fallback used to do: z is a different field
   * in different units, and once a colour field wins the whole picture that substitution goes
   * through the colour field's own scale. A readiness ratio against an annual cost puts every
   * scored component in the first band and gives the rest of the ramp to the unscored ones.
   */
  hasColor: boolean;
};

/** Hollow wherever a selected field has no value for this component, so it claims none. */
const isHollowMark = ({ hasSize, hasColor }: PlacedMark): boolean =>
  !(hasSize && hasColor);

/** How far outside its own edge a mark's rings sit, which a hollow mark's own stroke pushes out. */
const toRingOffset = (isHollow: boolean): number =>
  isHollow ? (HOLLOW_MARK_WIDTH + SEPARATION_RING_WIDTH) / 2 : 0;

const normalise = (value: number, [min, max]: NumericRange): number =>
  (value - min) / (max - min || 1);

export type MarkRanges = {
  xRange: NumericRange;
  yRange: NumericRange;
  zRange: NumericRange;
};

/** Where a mark sits in the unit cube: x and y across -1..1, z up 0..1. */
export const toMarkUnitPoint = (
  { x, y, z }: Pick<XyzDataPoint, 'x' | 'y' | 'z'>,
  { xRange, yRange, zRange }: MarkRanges
): UnitPoint => [
  normalise(x, xRange) * 2 - 1,
  normalise(y, yRange) * 2 - 1,
  normalise(z, zRange),
];

/**
 * Sizes a mark by area rather than by radius.
 *
 * Mapping the field to the radius squares the apparent difference, so a value twice
 * another looks four times as big. This is the same convention the bubble chart already
 * uses for its size field, so a component keeps its relative weight when a viewer switches
 * between the two charts.
 */
/**
 * Whether a size field is in play at all, read from the data rather than from the variant.
 *
 * `showSizeVariation` says which of the two charts this is, not whether a field was chosen - it
 * comes from the registration and is deliberately not persisted. Testing it in place of this
 * collapsed the two cases below on the bubble: with no size field selected, which is the state the
 * view opens in, every component carries `radius: null` and every mark read as unmeasured.
 *
 * Derived the same way the colour channel derives its own question, three lines further down, so
 * the two channels answer it from the same place.
 */
export const hasSizeField = (points: XyzDataPoint[]): boolean =>
  points.some(({ radius }) => radius !== null);

/**
 * Whether a mark's size is a measurement or the absence of one.
 *
 * Two different things reach `radius === null`: no size field is selected at all, where a uniform
 * size is right and every mark takes it, or this component has no value for the field that is
 * selected - which on a sparsely populated Ardoq field is routine. `toXyzDataPoints` keeps them
 * distinguishable on purpose and the renderer is where that distinction was being thrown away.
 */
const isMissingSize = (
  radius: number | null,
  style: Points3dStyle,
  sized: boolean
): boolean => style.showSizeVariation && sized && radius === null;

export const toDiameter = (
  radius: number | null,
  radiusRange: NumericRange,
  style: Points3dStyle,
  sized: boolean
): number => {
  /* At the floor rather than above it. The old fallback was 1.5x the minimum, which is 2.25x its
     area - so on a chart where size means "more", a component with no value outranked the
     smallest 15% of the ones that had one. */
  if (isMissingSize(radius, style, sized)) return MINIMUM_DIAMETER;
  if (!style.showSizeVariation || radius === null)
    return MINIMUM_DIAMETER * 1.5;

  const minimumArea = Math.PI * (MINIMUM_DIAMETER / 2) ** 2;
  const maximumArea = Math.PI * (style.maximumDiameter / 2) ** 2;
  const area =
    minimumArea + normalise(radius, radiusRange) * (maximumArea - minimumArea);

  return 2 * Math.sqrt(area / Math.PI);
};

/** How far along a plane's axis a footprint is sampled, in unit-cube units. */
const PLANE_SAMPLE = 0.05;

/** A light lower than this over the floor would throw shadows off to infinity. */
const MINIMUM_LIGHT_ELEVATION = 0.25;

type Project = ReturnType<typeof createProjector>;

/** Where a point in a plane lands on screen, and the screen vectors of the plane's two unit axes. */
type PlaneFrame = {
  screenX: number;
  screenY: number;
  aX: number;
  aY: number;
  bX: number;
  bY: number;
  /** Screen pixels per unit along the plane, averaged over its two axes. */
  unitLength: number;
};

const toPlaneFrame = (
  project: Project,
  origin: UnitPoint,
  axisA: UnitPoint,
  axisB: UnitPoint
): PlaneFrame => {
  const at = (step: UnitPoint) =>
    project(
      origin[0] + step[0] * PLANE_SAMPLE,
      origin[1] + step[1] * PLANE_SAMPLE,
      origin[2] + step[2] * PLANE_SAMPLE
    );
  const centre = project(...origin);
  const a = at(axisA);
  const b = at(axisB);
  const aX = (a.screenX - centre.screenX) / PLANE_SAMPLE;
  const aY = (a.screenY - centre.screenY) / PLANE_SAMPLE;
  const bX = (b.screenX - centre.screenX) / PLANE_SAMPLE;
  const bY = (b.screenY - centre.screenY) / PLANE_SAMPLE;
  return {
    screenX: centre.screenX,
    screenY: centre.screenY,
    aX,
    aY,
    bX,
    bY,
    unitLength: (Math.hypot(aX, aY) + Math.hypot(bX, bY)) / 2,
  };
};

type PlaneDisc = {
  layer: CanvasRenderingContext2D;
  base: DOMMatrix;
  frame: PlaneFrame;
  /** In plane units. */
  radius: number;
  softness: number;
  color: string;
  alpha: number;
};

/** A soft-edged disc lying in a plane, drawn through the plane's own axes so it foreshortens with it. */
const drawPlaneDisc = ({
  layer,
  base,
  frame,
  radius,
  softness,
  color,
  alpha,
}: PlaneDisc): void => {
  if (!(radius > 0) || alpha <= 0) return;
  layer.setTransform(base);
  layer.transform(
    frame.aX,
    frame.aY,
    frame.bX,
    frame.bY,
    frame.screenX,
    frame.screenY
  );
  const gradient = layer.createRadialGradient(0, 0, 0, 0, 0, radius);
  gradient.addColorStop(0, color);
  gradient.addColorStop(Math.max(0, Math.min(0.999, 1 - softness)), color);
  gradient.addColorStop(1, 'rgba(0, 0, 0, 0)');
  layer.globalAlpha = alpha;
  layer.fillStyle = gradient;
  layer.beginPath();
  layer.arc(0, 0, radius, 0, Math.PI * 2);
  layer.fill();
};

type PlaneRing = Omit<PlaneDisc, 'softness'> & {
  /** In plane units. */
  lineWidth: number;
};

/** An outline lying in a plane, for a mark that is hollow because it has no value to show. */
const drawPlaneRing = ({
  layer,
  base,
  frame,
  radius,
  lineWidth,
  color,
  alpha,
}: PlaneRing): void => {
  if (!(radius > 0) || alpha <= 0) return;
  layer.setTransform(base);
  layer.transform(
    frame.aX,
    frame.aY,
    frame.bX,
    frame.bY,
    frame.screenX,
    frame.screenY
  );
  layer.globalAlpha = alpha;
  layer.strokeStyle = color;
  layer.lineWidth = lineWidth;
  layer.beginPath();
  layer.arc(0, 0, radius, 0, Math.PI * 2);
  layer.stroke();
};

/** Clips `layer` to a quad given in unit-cube corners, in drawing order. */
const clipToQuad = (
  layer: CanvasRenderingContext2D,
  base: DOMMatrix,
  project: Project,
  corners: UnitPoint[]
): void => {
  layer.setTransform(base);
  layer.beginPath();
  corners.forEach((corner, index) => {
    const { screenX, screenY } = project(...corner);
    if (index === 0) layer.moveTo(screenX, screenY);
    else layer.lineTo(screenX, screenY);
  });
  layer.closePath();
  layer.clip();
};

/** The layer planar marks are drawn into before being laid down once, reused across frames. */
const planeLayer: { canvas: HTMLCanvasElement | null } = { canvas: null };

const toPlaneLayer = (
  width: number,
  height: number
): CanvasRenderingContext2D | null => {
  if (typeof document === 'undefined') return null;
  const canvas = planeLayer.canvas ?? document.createElement('canvas');
  planeLayer.canvas = canvas;
  if (canvas.width !== width || canvas.height !== height) {
    canvas.width = width;
    canvas.height = height;
  }
  const layer = canvas.getContext('2d');
  layer?.setTransform(1, 0, 0, 1, 0, 0);
  layer?.clearRect(0, 0, width, height);
  return layer;
};

const layDown = (
  context: CanvasRenderingContext2D,
  layer: CanvasRenderingContext2D,
  opacity: number
): void => {
  context.save();
  context.setTransform(1, 0, 0, 1, 0, 0);
  context.globalAlpha = opacity;
  context.drawImage(layer.canvas, 0, 0);
  context.restore();
};

/** A sun fixed over the plot, toward the light, for shadows that turn with the floor. */
const WORLD_SUN: UnitPoint = (() => {
  const direction: UnitPoint = [-0.45, 0.35, 0.82];
  const length = Math.hypot(...direction);
  return [direction[0] / length, direction[1] / length, direction[2] / length];
})();

/** The direction shadows are cast along, toward the light: overhead, leaning toward the chosen light. */
const toShadowLight = (
  camera: CameraState,
  { light, lightSpace }: MarkShadowLook
): UnitPoint => {
  const key =
    lightSpace === 'world'
      ? WORLD_SUN
      : toWorldLightDirection(
          KEY_LIGHT,
          getCameraBasis(camera.yaw, clampPitch(camera.pitch))
        );
  if (light.kind === 'fixed') {
    const across = Math.hypot(key[0], key[1]) || 1;
    return [
      (key[0] / across) * Math.cos(light.elevation),
      (key[1] / across) * Math.cos(light.elevation),
      Math.sin(light.elevation),
    ];
  }
  const { lean } = light;
  const blended: UnitPoint = [
    key[0] * lean,
    key[1] * lean,
    1 - lean + key[2] * lean,
  ];
  const length = Math.hypot(...blended) || 1;
  const direction: UnitPoint = [
    blended[0] / length,
    blended[1] / length,
    blended[2] / length,
  ];
  return direction[2] >= MINIMUM_LIGHT_ELEVATION
    ? direction
    : [direction[0], direction[1], MINIMUM_LIGHT_ELEVATION];
};

type ShadowArgs = {
  context: CanvasRenderingContext2D;
  marks: PlacedMark[];
  project: Project;
  camera: CameraState;
  shadow: MarkShadowLook;
  hasHighlight: boolean;
};

/** One of the two back walls: the plane `axis = position`, a direction along it, and its corners. */
type BackWall = {
  axis: 0 | 1;
  position: number;
  along: UnitPoint;
  corners: UnitPoint[];
};

/** The two walls behind the plot. Which they are follows the camera, as the axis box does. */
const toBackWalls = (yaw: number): BackWall[] => {
  const [backX, backY] = getBackCorner(yaw);
  return [
    {
      axis: 0,
      position: backX,
      along: [0, 1, 0],
      corners: [
        [backX, -1, 0],
        [backX, 1, 0],
        [backX, 1, 1],
        [backX, -1, 1],
      ],
    },
    {
      axis: 1,
      position: backY,
      along: [1, 0, 0],
      corners: [
        [-1, backY, 0],
        [1, backY, 0],
        [1, backY, 1],
        [-1, backY, 1],
      ],
    },
  ];
};

/** A step up a wall as long, in the projector's world, as a step along it. */
const toWallUp = (heightScale: number): UnitPoint => [0, 0, 1 / heightScale];

type ShadowDisc = {
  layer: CanvasRenderingContext2D;
  base: DOMMatrix;
  frame: PlaneFrame;
  mark: PlacedMark;
  /** 0 to 1: how far the shadow fell, which spreads it wider and fainter. */
  lift: number;
  shadow: MarkShadowLook;
  hasHighlight: boolean;
};

/** One mark's shadow on a plane, spread and faded by how far it fell. */
const drawShadowDisc = ({
  layer,
  base,
  frame,
  mark,
  lift,
  shadow,
  hasHighlight,
}: ShadowDisc): void => {
  const spread = lift * shadow.heightSpread;
  drawPlaneDisc({
    layer,
    base,
    frame,
    radius: ((mark.diameter / 2) * (1 + spread)) / frame.unitLength,
    softness: shadow.softness,
    color: 'rgba(0, 0, 0, 1)',
    alpha:
      (hasHighlight && !mark.isHighlighted ? DIMMED : 1) / (1 + spread * 2),
  });
};

/**
 * A soft ellipse on the floor under every mark, drawn before any of them.
 *
 * Cast along `toShadowLight`, clipped to the floor, and drawn into one layer laid down at `opacity`
 * so overlapping shadows merge rather than darken toward black. Higher marks cast wider, fainter
 * shadows.
 */
const renderFloorShadows = ({
  context,
  marks,
  project,
  camera,
  shadow,
  hasHighlight,
}: ShadowArgs): void => {
  const layer = toPlaneLayer(context.canvas.width, context.canvas.height);
  if (!layer) return;
  const base = context.getTransform();
  const light = toShadowLight(camera, shadow);

  layer.save();
  clipToQuad(layer, base, project, [
    [-1, -1, 0],
    [1, -1, 0],
    [1, 1, 0],
    [-1, 1, 0],
  ]);

  for (const mark of marks) {
    const [x, y, z] = mark.unit;
    /* Along the light to the floor, in the projector's world, where z is scaled by heightScale. */
    const drop = (z * camera.heightScale) / light[2];
    const frame = toPlaneFrame(
      project,
      [x - light[0] * drop, y - light[1] * drop, 0],
      [1, 0, 0],
      [0, 1, 0]
    );
    if (!(frame.unitLength > 0)) continue;

    drawShadowDisc({
      layer,
      base,
      frame,
      mark,
      lift: Math.max(0, Math.min(1, z)),
      shadow,
      hasHighlight,
    });
  }

  layer.restore();
  layDown(context, layer, shadow.opacity);
};

/**
 * The floor shadows' light, carried on to the two back walls.
 *
 * Each mark is followed along the light, away from it, to the wall plane. A wall the light does not
 * shine toward gets nothing, and neither does a mark whose path reaches the floor first, since the
 * floor shadow already stands for it. Every disc goes into one layer laid down once, so shadows
 * that overlap merge into one grey shape, as shadows do, rather than stacking.
 */
const renderWallShadows = ({
  context,
  marks,
  project,
  camera,
  shadow,
  hasHighlight,
}: ShadowArgs): void => {
  const layer = toPlaneLayer(context.canvas.width, context.canvas.height);
  if (!layer) return;
  const base = context.getTransform();
  const light = toShadowLight(camera, shadow);
  const { heightScale } = camera;
  const up = toWallUp(heightScale);

  for (const wall of toBackWalls(camera.yaw)) {
    const toward = light[wall.axis];
    if (toward === 0) continue;
    layer.save();
    clipToQuad(layer, base, project, wall.corners);
    for (const mark of marks) {
      const [x, y, z] = mark.unit;
      const distance = (mark.unit[wall.axis] - wall.position) / toward;
      if (distance <= 0) continue;
      /* Along the light in the projector's world, where z is scaled by heightScale. */
      const height = z * heightScale - light[2] * distance;
      if (height < 0) continue;
      const frame = toPlaneFrame(
        project,
        [
          x - light[0] * distance,
          y - light[1] * distance,
          height / heightScale,
        ],
        wall.along,
        up
      );
      if (!(frame.unitLength > 0)) continue;

      drawShadowDisc({
        layer,
        base,
        frame,
        mark,
        lift: Math.max(0, Math.min(1, distance / 2)),
        shadow,
        hasHighlight,
      });
    }
    layer.restore();
  }

  layDown(context, layer, shadow.opacity);
};

type WallProjectionArgs = {
  context: CanvasRenderingContext2D;
  marks: PlacedMark[];
  project: Project;
  camera: CameraState;
  wallProjection: WallProjectionLook;
  hasHighlight: boolean;
};

/**
 * Every mark flattened onto the two back walls: x against height on one, y against height on the
 * other. Which walls are the back ones follows the camera, as the axis box does.
 */
const renderWallProjections = ({
  context,
  marks,
  project,
  camera,
  wallProjection,
  hasHighlight,
}: WallProjectionArgs): void => {
  const layer = toPlaneLayer(context.canvas.width, context.canvas.height);
  if (!layer) return;
  const base = context.getTransform();
  const up = toWallUp(camera.heightScale);

  for (const wall of toBackWalls(camera.yaw)) {
    layer.save();
    clipToQuad(layer, base, project, wall.corners);
    for (const mark of marks) {
      const at: UnitPoint =
        wall.axis === 0
          ? [wall.position, mark.unit[1], mark.unit[2]]
          : [mark.unit[0], wall.position, mark.unit[2]];
      const frame = toPlaneFrame(project, at, wall.along, up);
      if (!(frame.unitLength > 0)) continue;
      const radius = mark.diameter / 2 / frame.unitLength;
      const dimming = hasHighlight && !mark.isHighlighted ? DIMMED : 1;
      if (isHollowMark(mark)) {
        const ring = {
          layer,
          base,
          frame,
          radius,
          lineWidth: HOLLOW_MARK_WIDTH / frame.unitLength,
        };
        drawPlaneRing({
          ...ring,
          color: 'rgba(0, 0, 0, 1)',
          alpha: (1 - wallProjection.tint) * dimming,
        });
        drawPlaneRing({
          ...ring,
          color: mark.color,
          alpha: wallProjection.tint * dimming,
        });
        continue;
      }
      drawPlaneDisc({
        layer,
        base,
        frame,
        radius,
        softness: wallProjection.softness,
        color: 'rgba(0, 0, 0, 1)',
        alpha: (1 - wallProjection.tint) * dimming,
      });
      drawPlaneDisc({
        layer,
        base,
        frame,
        radius,
        softness: wallProjection.softness,
        color: mark.color,
        alpha: wallProjection.tint * dimming,
      });
    }
    layer.restore();
  }

  layDown(context, layer, wallProjection.opacity);
};

type DepthRange = { nearest: number; span: number };

const toDepthRange = (marks: PlacedMark[]): DepthRange => {
  const { nearest, farthest } = marks.reduce(
    (range, { depth }) => ({
      nearest: Math.max(range.nearest, depth),
      farthest: Math.min(range.farthest, depth),
    }),
    { nearest: -Infinity, farthest: Infinity }
  );
  return { nearest, span: nearest - farthest || 1 };
};

/** The part of a mark a contact shadow is measured against. */
type ContactMark = Pick<
  PlacedMark,
  'screenX' | 'screenY' | 'diameter' | 'depth'
>;

export type ContactReceiver<Mark extends ContactMark> = {
  mark: Mark;
  /** 1 where the receiver is right behind the caster, falling to 0 at the depth range. */
  weight: number;
};

const overlapsOnScreen = (a: ContactMark, b: ContactMark, reach: number) =>
  Math.hypot(a.screenX - b.screenX, a.screenY - b.screenY) <
  reach + b.diameter / 2;

/**
 * The marks a caster's contact shadow falls on: behind it, within the depth range, and inside its
 * reach on screen, each weighted by how close behind it is.
 */
export const toContactReceivers = <Mark extends ContactMark>(
  caster: ContactMark,
  candidates: readonly Mark[],
  { spread, depthRange }: MarkContactLook
): ContactReceiver<Mark>[] => {
  const reach = (caster.diameter / 2) * (1 + spread);
  return candidates.flatMap(candidate => {
    const gap = caster.depth - candidate.depth;
    if (gap <= 0 || gap >= depthRange) return [];
    if (!overlapsOnScreen(caster, candidate, reach)) return [];
    return [{ mark: candidate, weight: 1 - gap / depthRange }];
  });
};

/**
 * The marks drawn over `receiver` since it was drawn: later in `drawOrder` and overlapping it on
 * screen. A shadow on the receiver must stay off them, since what shows there is them, not it.
 */
export const toCoveringMarks = <Mark extends ContactMark>(
  receiver: Mark,
  candidates: readonly Mark[],
  drawOrder: (mark: Mark) => number
): Mark[] =>
  candidates.filter(
    candidate =>
      drawOrder(candidate) > drawOrder(receiver) &&
      overlapsOnScreen(candidate, receiver, candidate.diameter / 2)
  );

type ContactArgs = {
  context: CanvasRenderingContext2D;
  mark: PlacedMark;
  /** What the shadow falls on, each with the marks since drawn over it. Unused with the halo. */
  receivers: readonly (ContactReceiver<PlacedMark> & {
    covering: readonly PlacedMark[];
  })[];
  contact: MarkContactLook;
  markAlpha: number;
};

/** Far enough outside any canvas that a rectangle this size contains the whole frame. */
const EVERYWHERE = 1e6;

/**
 * The ring between a mark's edge and `reach`, filled with the current style. Never the disc inside,
 * which the mark is drawn over at less than full opacity, so anything there would show through it.
 */
const fillRing = (
  context: CanvasRenderingContext2D,
  mark: PlacedMark,
  radius: number,
  reach: number
): void => {
  context.beginPath();
  context.arc(mark.screenX, mark.screenY, reach, 0, Math.PI * 2);
  context.arc(mark.screenX, mark.screenY, radius, 0, Math.PI * 2);
  context.fill('evenodd');
};

/**
 * The shadow a mark casts onto what is behind it, drawn before the mark so the mark covers it.
 *
 * A radial gradient from the mark's edge out to `spread` radii, dark to clear. With the halo it is
 * laid down once over everything. Without it, once per receiver, clipped to the part of the
 * receiver still showing: its disc, less every mark drawn over it since. A canvas clip can only
 * intersect, so each covering mark is removed by intersecting with everything but its disc.
 */
const renderContactShadow = ({
  context,
  mark,
  receivers,
  contact,
  markAlpha,
}: ContactArgs): void => {
  if (!contact.halo && receivers.length === 0) return;

  const radius = mark.diameter / 2;
  const reach = radius * (1 + contact.spread);
  const gradient = context.createRadialGradient(
    mark.screenX,
    mark.screenY,
    radius,
    mark.screenX,
    mark.screenY,
    reach
  );
  gradient.addColorStop(0, 'rgba(0, 0, 0, 1)');
  gradient.addColorStop(0.4, 'rgba(0, 0, 0, 0.45)');
  gradient.addColorStop(1, 'rgba(0, 0, 0, 0)');
  context.fillStyle = gradient;

  if (contact.halo) {
    context.globalAlpha = markAlpha * contact.opacity;
    fillRing(context, mark, radius, reach);
    return;
  }

  for (const { mark: receiver, weight, covering } of receivers) {
    context.save();
    context.beginPath();
    context.arc(
      receiver.screenX,
      receiver.screenY,
      receiver.diameter / 2,
      0,
      Math.PI * 2
    );
    context.clip();
    for (const cover of covering) {
      context.beginPath();
      context.rect(-EVERYWHERE, -EVERYWHERE, EVERYWHERE * 2, EVERYWHERE * 2);
      context.arc(
        cover.screenX,
        cover.screenY,
        cover.diameter / 2,
        0,
        Math.PI * 2
      );
      context.clip('evenodd');
    }
    context.globalAlpha = markAlpha * contact.opacity * weight;
    fillRing(context, mark, radius, reach);
    context.restore();
  }
};

type ScreenBuckets = {
  add: (mark: PlacedMark) => void;
  /** Every mark added in the 3x3 block of cells around this one. */
  near: (mark: PlacedMark) => PlacedMark[];
};

/** Marks bucketed by screen cell, so a contact shadow tests the marks near it rather than all. */
const toScreenBuckets = (cellSize: number): ScreenBuckets => {
  const cells = new Map<string, PlacedMark[]>();
  const cellOf = (value: number) => Math.floor(value / cellSize);
  return {
    add: mark => {
      const key = `${cellOf(mark.screenX)},${cellOf(mark.screenY)}`;
      const cell = cells.get(key);
      if (cell) {
        cell.push(mark);
        return;
      }
      cells.set(key, [mark]);
    },
    near: mark => {
      const column = cellOf(mark.screenX);
      const row = cellOf(mark.screenY);
      return [-1, 0, 1].flatMap(dx =>
        [-1, 0, 1].flatMap(dy => cells.get(`${column + dx},${row + dy}`) ?? [])
      );
    },
  };
};

type ContactState = {
  /** Marks drawn so far that can receive a contact shadow. */
  receivers: ScreenBuckets;
  /** Marks drawn so far that hide what is behind them, and so can cover a receiver. */
  covers: ScreenBuckets;
  orderOf: (mark: PlacedMark) => number;
  add: (mark: PlacedMark, how: { dimmed: boolean; isHollow: boolean }) => void;
};

/**
 * What the contact shadows need to remember across the pass, made only when they are on.
 *
 * A hollow mark neither receives nor covers: it is a ring, and inside it is whatever lies behind.
 * A dimmed mark covers without receiving, since it is drawn over what is behind it but must not be
 * shaded back toward visibility.
 */
const toContactState = (
  marks: readonly PlacedMark[],
  contact: MarkContactLook
): ContactState => {
  const largestDiameter = marks.reduce(
    (largest, { diameter }) => Math.max(largest, diameter),
    0
  );
  /* A cell as wide as the farthest apart a shadow's source and a mark it falls on can be, so every
     candidate is in the 3x3 block around a mark. */
  const cellSize = Math.max(1, largestDiameter * (1 + contact.spread / 2));
  const receivers = toScreenBuckets(cellSize);
  const covers = toScreenBuckets(cellSize);
  const drawOrder = new Map<PlacedMark, number>();
  return {
    receivers,
    covers,
    orderOf: mark => drawOrder.get(mark) ?? -1,
    add: (mark, { dimmed, isHollow }) => {
      if (isHollow) return;
      drawOrder.set(mark, drawOrder.size);
      covers.add(mark);
      if (!dimmed) receivers.add(mark);
    },
  };
};

type OutlineArgs = {
  context: CanvasRenderingContext2D;
  mark: PlacedMark;
  isHollow: boolean;
  outline: MarkOutlineLook;
  surfaceColor: string;
  markAlpha: number;
};

const renderOutline = ({
  context,
  mark,
  isHollow,
  outline,
  surfaceColor,
  markAlpha,
}: OutlineArgs): void => {
  if (outline.kind === 'none' || outline.width <= 0) return;

  const radius = mark.diameter / 2;
  context.globalAlpha = markAlpha * outline.opacity;
  context.lineWidth = outline.width;

  /* A ring in the surface colour separates overlapping marks without adding a second colour that
     could be mistaken for encoding. Outside a hollow mark's own stroke, so the absence cue shows. */
  if (outline.kind === 'surface' || isHollow) {
    context.beginPath();
    context.arc(
      mark.screenX,
      mark.screenY,
      radius + toRingOffset(isHollow),
      0,
      Math.PI * 2
    );
    context.strokeStyle = surfaceColor;
    context.stroke();
    context.globalAlpha = markAlpha;
    return;
  }

  /* Inside the edge, so the rim adds no area to a mark whose area is the encoding. */
  context.beginPath();
  context.arc(
    mark.screenX,
    mark.screenY,
    Math.max(0, radius - outline.width / 2),
    0,
    Math.PI * 2
  );
  context.strokeStyle = mark.color;
  context.stroke();
  context.strokeStyle = 'rgba(0, 0, 0, 0.35)';
  context.stroke();
  context.globalAlpha = markAlpha;
};

/**
 * Draws points in field space, far marks first.
 *
 * Sorting by depth is what makes overlap read correctly: without it, paint order follows
 * component order and a mark at the back of the plane can cover one at the front, which
 * silently inverts the depth the whole chart is meant to convey.
 */
export const renderPoints3d = ({
  context,
  points,
  camera,
  viewport,
  colorScale,
  style,
  xRange,
  yRange,
  zRange,
  radiusRange,
  surfaceColor,
  inkColor,
  highlightedIds,
  emphasisedIds,
  look = DEFAULT_POINTS_LOOK,
}: Points3dRenderArgs): void => {
  const project = createProjector(camera, viewport);

  /* The ramp evaluated from a table rather than per mark. `forValue` is nine `Math.pow` calls and
     an allocation, and this is the third renderer to pay it once per element - the chart with no
     GPU path and no reduced-scale fallback, repainting through every frame of an orbit. */
  const ramp = createRampLookup(colorScale);

  /* One CSS string per distinct table row rather than per mark. The table has at most `bands`
     rows - 256 at the persisted `bands: 0` - and `nearest` hands back the row itself, so keying on
     that reference is exact and cannot go stale. Building the string per mark was 150,000
     allocations a paint for at most 256 results. */
  const cssByEntry = new Map<Rgb, string>();
  const rampColor = (value: number): string => {
    const entry = ramp.nearest(value);
    const cached = cssByEntry.get(entry);
    if (cached !== undefined) return cached;

    const css = toCssColor(entry);
    cssByEntry.set(entry, css);
    return css;
  };

  /* Whether a colour field is in play at all, decided once for the picture rather than per mark.

     A selected colour field beats the component type's colour, because it is the thing the reader
     asked for and it is what the surface and the contour already do with the same selection.
     Deciding it per mark instead would colour the components that carry a value from the ramp and
     the rest from their type, in one legend-less picture - two encodings the reader cannot tell
     apart. With no field selected the type colour leads, and the ramp over z is the fallback for
     components whose type has none. */
  const colored = hasColorField(points);
  const sized = hasSizeField(points);

  const marks: PlacedMark[] = points.map(point => {
    const unit = toMarkUnitPoint(point, { xRange, yRange, zRange });
    const projected = project(...unit);

    return {
      unit,
      screenX: projected.screenX,
      screenY: projected.screenY,
      depth: projected.depth,
      /* Foreshortening applies to the mark as well as its position, so a near mark grows
         with its neighbourhood instead of floating free of it. */
      diameter:
        toDiameter(point.radius, radiusRange, style, sized) * projected.scale,
      hasSize: !isMissingSize(point.radius, style, sized),
      hasColor: !colored || point.colorValue !== null,
      color:
        colored && point.colorValue === null
          ? inkColor
          : colored
            ? rampColor(point.colorValue as number)
            : (point.fill ?? rampColor(point.z)),
      isHighlighted: highlightedIds?.has(point.uniqueId) ?? false,
      isEmphasised: emphasisedIds?.has(point.uniqueId) ?? false,
    };
  });

  marks.sort((left, right) => left.depth - right.depth);

  context.save();
  context.lineJoin = 'round';

  const hasHighlight = highlightedIds !== null && highlightedIds.size > 0;

  /* Once for the whole pass: the sprite depends on nothing that varies between marks. */
  const sphereSprite = getSphereSprite(look.shading);
  const toShadingStrength = shadingStrengthAbove(look.shading.minimumDiameter);

  if (look.wallProjection.opacity > 0) {
    renderWallProjections({
      context,
      marks,
      project,
      camera,
      wallProjection: look.wallProjection,
      hasHighlight,
    });
  }

  if (look.shadow.opacity > 0 && camera.pitch > 0) {
    renderFloorShadows({
      context,
      marks,
      project,
      camera,
      shadow: look.shadow,
      hasHighlight,
    });
  }

  if (
    look.shadow.opacity > 0 &&
    look.shadow.light.kind === 'fixed' &&
    look.shadow.light.onWalls
  ) {
    renderWallShadows({
      context,
      marks,
      project,
      camera,
      shadow: look.shadow,
      hasHighlight,
    });
  }

  const depthRange = look.depthFade > 0 ? toDepthRange(marks) : null;

  /* The blit is the one place this pass resamples an image, and by a large factor: `useChartCanvas`
     sets the transform to `devicePixelRatio`, so a 10px mark is the 128px sprite reduced eightfold.
     The default `imageSmoothingQuality` is `'low'` - bilinear off four texels, no mip chain - which
     makes the limb sample differently depending where a mark lands subpixel. Set here rather than
     left to whatever the caller had, and inside the pass's own `save`, which is the idiom
     `renderPhongFill` already uses for its scaled blit. */
  context.imageSmoothingEnabled = true;
  context.imageSmoothingQuality = 'high';

  const contactState =
    look.contact.opacity > 0 ? toContactState(marks, look.contact) : null;

  for (const mark of marks) {
    const dimmed = hasHighlight && !mark.isHighlighted;
    /* One expression for hollowness, read by the stroke below and by the ring's offset. Keyed
       separately they drifted the moment a second reason to be hollow arrived: the ring kept
       testing `hasSize`, so a mark hollow for want of a colour value took the un-offset arm and
       the 2-wide surface ring strictly contained its own 1.5-wide stroke - the round-12 defect
       through the other door, and on a scatter that is every such mark. */
    const isHollow = isHollowMark(mark);
    const markAlpha = dimmed ? DIMMED * style.opacity : style.opacity;

    /* Not from a dimmed mark, nor onto one: a mark faded out of a highlight should neither shade
       its neighbors nor be shaded back toward visibility. */
    if (contactState) {
      if (!dimmed && !isHollow) {
        renderContactShadow({
          context,
          mark,
          receivers: look.contact.halo
            ? []
            : toContactReceivers(
                mark,
                contactState.receivers.near(mark),
                look.contact
              ).map(receiver => ({
                ...receiver,
                covering: toCoveringMarks(
                  receiver.mark,
                  contactState.covers.near(receiver.mark),
                  contactState.orderOf
                ),
              })),
          contact: look.contact,
          markAlpha,
        });
      }
      contactState.add(mark, { dimmed, isHollow });
    }
    context.globalAlpha = markAlpha;

    context.beginPath();
    context.arc(mark.screenX, mark.screenY, mark.diameter / 2, 0, Math.PI * 2);
    if (!isHollow) {
      context.fillStyle = mark.color;
      context.fill();

      /* And light over the top, clipped to the same path, so the flat fill still carries the
         encoding and the shading only models the form. Not while dimmed: at `DIMMED` the mark is
         a hint that something is there, and shading a hint is paint spent on nothing. */
      const shading = dimmed ? 0 : toShadingStrength(mark.diameter);

      if (shading > 0 && sphereSprite) {
        const radius = mark.diameter / 2;
        /* Faded in across the band rather than switched on at it. The diameter is foreshortened,
           so a mark near the threshold crosses it as the plot turns, and a step there is a pop. */
        context.globalAlpha = markAlpha * shading;
        context.drawImage(
          sphereSprite,
          mark.screenX - radius,
          mark.screenY - radius,
          mark.diameter,
          mark.diameter
        );
        context.globalAlpha = markAlpha;
      }
    } else {
      /* Hollow wherever a selected field has no value for this component - the same idiom
         `renderSampleMarkers` uses for a sample with no surface beneath it. A filled mark would
         imply the chart has something to say about a channel it has not measured, and there is no
         legend to correct the impression. */
      context.strokeStyle = mark.color;
      context.lineWidth = HOLLOW_MARK_WIDTH;
      context.stroke();
    }

    if (!dimmed) {
      renderOutline({
        context,
        mark,
        isHollow,
        outline: look.outline,
        surfaceColor,
        markAlpha,
      });
    }

    if (!dimmed && depthRange) {
      context.globalAlpha =
        look.depthFade * ((depthRange.nearest - mark.depth) / depthRange.span);
      context.beginPath();
      context.arc(
        mark.screenX,
        mark.screenY,
        mark.diameter / 2 + toRingOffset(isHollow) + look.outline.width / 2,
        0,
        Math.PI * 2
      );
      context.fillStyle = surfaceColor;
      context.fill();
      context.globalAlpha = markAlpha;
    }

    /* Outside the separation ring, and at full strength even while something else is hovered: the
       component the reader arrived from should stay findable during the gesture that dims the
       rest, which is the case a permanent dimming was answering the wrong way. */
    if (mark.isEmphasised) {
      context.globalAlpha = style.opacity;
      context.beginPath();
      context.arc(
        mark.screenX,
        mark.screenY,
        mark.diameter / 2 +
          toRingOffset(isHollow) +
          (SEPARATION_RING_WIDTH + EMPHASIS_RING_WIDTH) / 2,
        0,
        Math.PI * 2
      );
      context.strokeStyle = inkColor;
      context.lineWidth = EMPHASIS_RING_WIDTH;
      context.stroke();
    }
  }

  context.restore();
};

/** Everything a mark's screen position and hit radius are derived from. */
export type MarkHitInputs = Pick<
  Points3dRenderArgs,
  | 'points'
  | 'camera'
  | 'viewport'
  | 'style'
  | 'xRange'
  | 'yRange'
  | 'zRange'
  | 'radiusRange'
>;

/** Every mark's screen position, depth and hit radius for one set of inputs, indexed like `points`. */
export type MarkHitCache = {
  inputs: MarkHitInputs;
  screenX: Float64Array;
  screenY: Float64Array;
  depth: Float64Array;
  hitRadius: Float64Array;
};

export const buildMarkHitCache = (inputs: MarkHitInputs): MarkHitCache => {
  const {
    points,
    camera,
    viewport,
    style,
    xRange,
    yRange,
    zRange,
    radiusRange,
  } = inputs;
  const project = createProjector(camera, viewport);
  /* The same question the renderer asks, so the hit target keeps matching the drawn diameter. */
  const sized = hasSizeField(points);
  const screenX = new Float64Array(points.length);
  const screenY = new Float64Array(points.length);
  const depth = new Float64Array(points.length);
  const hitRadius = new Float64Array(points.length);

  points.forEach((point, index) => {
    const projected = project(
      ...toMarkUnitPoint(point, { xRange, yRange, zRange })
    );
    const radius =
      (toDiameter(point.radius, radiusRange, style, sized) * projected.scale) /
      2;
    screenX[index] = projected.screenX;
    screenY[index] = projected.screenY;
    depth[index] = projected.depth;
    /* Hit target never smaller than the minimum mark, so tiny marks stay clickable. */
    hitRadius[index] = Math.max(radius, MINIMUM_DIAMETER / 2);
  });

  return { inputs, screenX, screenY, depth, hitRadius };
};

/** Whether `cache` still describes the marks `inputs` would draw. Opacity moves no mark. */
const isMarkHitCacheFor = (
  { inputs: built }: MarkHitCache,
  inputs: MarkHitInputs
): boolean =>
  built.points === inputs.points &&
  isSameCamera(built.camera, inputs.camera) &&
  built.viewport.width === inputs.viewport.width &&
  built.viewport.height === inputs.viewport.height &&
  built.style.showSizeVariation === inputs.style.showSizeVariation &&
  built.style.maximumDiameter === inputs.style.maximumDiameter &&
  isSameRange(built.xRange, inputs.xRange) &&
  isSameRange(built.yRange, inputs.yRange) &&
  isSameRange(built.zRange, inputs.zRange) &&
  isSameRange(built.radiusRange, inputs.radiusRange);

/** The cache for `inputs`: `cache` itself while it is still current, a fresh one otherwise. */
export const refreshMarkHitCache = (
  cache: MarkHitCache | null,
  inputs: MarkHitInputs
): MarkHitCache => {
  if (cache && isMarkHitCacheFor(cache, inputs)) return cache;
  return buildMarkHitCache(inputs);
};

/**
 * Finds the frontmost mark under the cursor.
 *
 * The greatest depth among the marks whose hit radius contains the cursor wins, and on a tie the
 * first such mark in `points` order - so the mark a viewer sees on top is the one they select.
 */
export const findMarkInCache = (
  { inputs: { points }, screenX, screenY, depth, hitRadius }: MarkHitCache,
  cursor: { x: number; y: number }
): XyzDataPoint | null => {
  let nearest = -1;
  let nearestDepth = -Infinity;

  for (let index = 0; index < points.length; index++) {
    if (depth[index] <= nearestDepth) continue;

    const radius = hitRadius[index];
    const dx = cursor.x - screenX[index];
    if (dx > radius || dx < -radius) continue;
    const dy = cursor.y - screenY[index];
    if (dy > radius || dy < -radius) continue;
    if (Math.hypot(dx, dy) > radius) continue;

    nearest = index;
    nearestDepth = depth[index];
  }

  if (nearest === -1) return null;
  return points[nearest];
};

/** {@link findMarkInCache} against a cache built for this one call. */
export const findMarkAt = ({
  cursor,
  ...inputs
}: MarkHitInputs & { cursor: { x: number; y: number } }): XyzDataPoint | null =>
  findMarkInCache(buildMarkHitCache(inputs), cursor);
